import { ASSET_TYPE } from 'src/data/asset';
import { GENERATION_TOOL, type GenerationTool } from 'src/data/generationTool';
import { mimicImageResource } from 'src/data/performance';
import type { ShortFormSection } from 'src/data/shortForm';
import { convertHtmlContentToPlainText } from 'src/lib/slate';
import type { BackendImproveSectionInput, BackendLongFormOutline } from 'src/types/backend';

import { IMPROVER_CONTENT_TYPE, type ImproverContentType } from './contentType';

/**
 * A part of the document the backend found in the content to improve. Its `status` says whether the
 * part is settled: an outline arrives as placeholders and fills in while the job runs.
 */
export type ImproverOutlineItem = BackendLongFormOutline;

type OutlineInputSection = Pick<BackendImproveSectionInput, 'assetType' | 'content'>;

// A character no content carries, so two sections cannot join into the digest of a third.
const DIGEST_SEPARATOR = '\u0000';

export const OUTLINE_ITEM_STATUS = {
  generating: 'generating',
  active: 'active',
} as const;

/**
 * A section of the outline. `status` says whether the backend has settled the part; a section the
 * user added has none, and is settled by definition.
 */
export type ImproverOutlineSection = ShortFormSection & { status?: string };

export const isOutlineSectionGenerating = (section: ImproverOutlineSection): boolean =>
  section.status === OUTLINE_ITEM_STATUS.generating;

export const isOutlineItemImage = (item: ImproverOutlineItem): boolean =>
  item.assetType === ASSET_TYPE.image;

/**
 * The improve panel edits sections, so an outline item becomes one. An item that is still being
 * generated has neither content nor a tool of its own yet, and stands in as an empty section until
 * the backend replaces it.
 */
export const outlineItemToSection = (item: ImproverOutlineItem): ImproverOutlineSection => {
  const section: ImproverOutlineSection = {
    id: item.id,
    name: item.name || '',
    // Generate derives a part's asset type from its tool, so a part with no tool of its own is sent
    // as freestyle rather than with none.
    // The two enums name the same tools but are declared apart, so the backend's value is read as
    // the frontend's.
    generationTool: (item.generationTool as GenerationTool) || GENERATION_TOOL.freestyle,
    status: item.status,
  };

  if (isOutlineItemImage(item)) {
    if (item.imageResourceId && item.imageUrl) {
      section.imageResource = mimicImageResource({
        imageResourceId: item.imageResourceId,
        path: item.imageUrl,
      });
    }
    return section;
  }

  section.content = item.content || '';
  return section;
};

export const outlineItemsToSections = (items: ImproverOutlineItem[]): ImproverOutlineSection[] =>
  [...items].sort((a, b) => a.order - b.order).map(outlineItemToSection);

// Letters or digits left once markup, comments, styles and entities are taken out. A body that has
// some is known to hold text without parsing it, which matters as it is checked on every change.
const hasTextOutsideTags = (html: string): boolean =>
  /[\p{L}\p{N}]/u.test(
    html
      .replace(/<(script|style)\b[\s\S]*?<\/\1>|<!--[\s\S]*?-->|<[^>]*>/gi, '')
      .replace(/&#?\w+;/g, '')
  );

const isOutlineBodyEmpty = (html: string | undefined): boolean => {
  const body = html || '';
  if (/<img/i.test(body) || hasTextOutsideTags(body)) {
    return false;
  }

  return !convertHtmlContentToPlainText(body).trim();
};

/**
 * What an extraction was asked for. Comparing it against the content on screen is how a second
 * extraction of the same text is avoided, and how Generate tells whether the outline it holds
 * belongs to what the user is looking at.
 *
 * An input with no body has no digest: the backend rejects an extraction without content. A body
 * is empty when it has no text and no image; an empty editor holds `<p></p>`, not an empty string.
 */
export const outlineDigest = (
  contentType: ImproverContentType,
  sections: Pick<ShortFormSection, 'content'>[]
): string | null => {
  const parts = sections.map((section) => section.content || '');
  if (isOutlineBodyEmpty(parts[parts.length - 1])) {
    return null;
  }

  return [contentType, ...parts].join(DIGEST_SEPARATOR);
};

/**
 * What the extraction is given: the document as one section, and for an email its subject ahead of
 * the body. The backend reads the asset type and the content of each, and fails on any other count.
 */
export const outlineInputSections = (
  contentType: ImproverContentType,
  sections: Pick<ShortFormSection, 'content'>[]
): OutlineInputSection[] => {
  const body = {
    assetType: ASSET_TYPE.canvasFreestyle,
    content: sections[sections.length - 1]?.content || '',
  };

  if (contentType !== IMPROVER_CONTENT_TYPE.email) {
    return [body];
  }

  return [{ assetType: ASSET_TYPE.emailSubject, content: sections[0]?.content || '' }, body];
};
